import { useState, useCallback, useEffect } from 'react';
import { fetchPlaces, fetchPlacesSync } from '../services/placesService';
import type { Place } from '../types/Place';

/**
 * Internal state structure for places data
 */
interface PlacesData {
  places: Place[];
  stats: { countriesCount: number; placesCount: number };
  error: Error | null;
}

/**
 * Initialize places data synchronously for immediate availability
 * This ensures the hook has data on first render
 */
function initializePlaces(): PlacesData {
  try {
    const result = fetchPlacesSync();
    return {
      places: result.places,
      stats: result.stats,
      error: null,
    };
  } catch (err) {
    console.error('Failed to load places data:', err);
    return {
      places: [],
      stats: { countriesCount: 0, placesCount: 0 },
      error: err instanceof Error ? err : new Error('Unknown error loading places'),
    };
  }
}

// Initialize once at module load for immediate availability
const initialData = initializePlaces();

export interface UsePlacesResult {
  /** Array of validated and sorted places */
  places: Place[];
  /** Lookup a place by its ID */
  getPlaceById: (id: string) => Place | undefined;
  /** Statistics about the loaded places */
  stats: { countriesCount: number; placesCount: number };
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Error if data failed to load */
  error: Error | null;
  /** Retry loading data after an error (returns Promise for awaiting) */
  retry: () => Promise<void>;
  /** Whether a retry is available (has error and not loading) */
  canRetry: boolean;
  /** Refresh data from source (async) */
  refresh: () => Promise<void>;
}

/**
 * Hook for accessing places data
 *
 * Features:
 * - Immediate data availability via sync initialization
 * - Async refresh capability for API-based fetching
 * - Retry functionality for error recovery
 * - Memoized getPlaceById for performance
 * - Optional mapId parameter for filtering by map
 *
 * @param mapId - Optional map ID to filter places by (for API mode)
 * @example
 * const { places, isLoading, error, retry } = usePlaces();
 * const { places } = usePlaces('map-123'); // With mapId filter
 */
export function usePlaces(mapId?: string): UsePlacesResult {
  const [data, setData] = useState<PlacesData>(initialData);
  const [isLoading, setIsLoading] = useState(false);

  const getPlaceById = useCallback((id: string): Place | undefined => {
    return data.places.find(p => p.id === id);
  }, [data.places]);

  /**
   * Refresh data asynchronously from the configured source
   * Uses the service layer which can be configured for static or API fetching
   */
  const refresh = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await fetchPlaces(mapId);
      setData({
        places: result.places,
        stats: result.stats,
        error: null,
      });
    } catch (err) {
      console.error('Failed to refresh places data:', err);
      setData(prev => ({
        ...prev,
        error: err instanceof Error ? err : new Error('Unknown error refreshing places'),
      }));
    } finally {
      setIsLoading(false);
    }
  }, [mapId]);

  // Re-fetch when mapId changes
  useEffect(() => {
    if (mapId) {
      refresh();
    }
  }, [mapId, refresh]);

  /**
   * Retry loading data after an error
   * Wraps refresh for semantic clarity, returns Promise for awaiting
   */
  const retry = useCallback(() => {
    return refresh();
  }, [refresh]);

  const canRetry = data.error !== null && !isLoading;

  return {
    places: data.places,
    getPlaceById,
    stats: data.stats,
    isLoading,
    error: data.error,
    retry,
    canRetry,
    refresh,
  };
}
